import { useEffect, useState } from "react";
import { Heading } from "../components/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/table";
import { Button } from "../components/button";
import { BSHorse, HorseLocation } from "@/models/horse";
import { collection, deleteDoc, doc, onSnapshot, query, setDoc, Timestamp, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import EmptyState from "./components/empty-state";
import { EllipsisHorizontalIcon, PlusIcon, ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { formatDateToInputString } from "../helpers/date.helpers";
import { Dropdown, DropdownButton, DropdownDivider, DropdownItem, DropdownMenu } from "../components/dropdown";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "../components/dialog";
import { Fieldset, Label } from "../components/fieldset";
import * as Headless from "@headlessui/react";
import { Input } from "../components/input";
import { Checkbox } from "../components/checkbox";
import { Select } from "../components/select";
import { formatCurrency } from "../helpers/currency.helpers";
import { BSCustomer } from "@/models/customer";
import { orderBy } from "firebase/firestore";
import { Switch, SwitchField } from "../components/switch";
import { useTableSort } from '../hooks/useTableSort';
import { useColumnVisibility, ColumnConfig } from '../hooks/useColumnVisibility';
import { useTableSearch } from '../hooks/useTableSearch';
import { BSBillItem } from "@/models/billItem";
import { Alert, AlertActions, AlertDescription, AlertTitle } from "../components/alert";
import { InlineDatePicker } from "./components/inline-date-picker";

type SortField = keyof BSHorse;

export default function Horses() {
  const collectionName = "horses";
  const emptyObject: BSHorse = {
    id: "",
    customerId: "",
    name: "",
    checkInDate: new Date(),
    checkOutDate: null,
    boarding: 2900,
    boardingSurcharge: 150,
    hasBlanket: false,
    training: 1750,
    isBillable: true,
    location: 'florida',
  };

  // Dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Billing
  const [isBillDialogOpen, setIsBillDialogOpen] = useState(false);
  const [isBilling, setIsBilling] = useState(false);

  // Filtering
  let [inProperty, setInProperty] = useState(true)

  // Data
  const [newObject, setNewObject] = useState<BSHorse>(emptyObject);
  const [objects, setObjects] = useState<BSHorse[]>([]);
  const [customers, setCustomers] = useState<BSCustomer[]>([]);

  // Sorting
  const columns: ColumnConfig<BSHorse>[] = [
    { field: 'name', label: 'Name', defaultVisible: true },
    { field: 'customerId', label: 'Responsible Party', defaultVisible: true },
    { field: 'checkInDate', label: 'Check-in Date', defaultVisible: true },
    { field: 'checkOutDate', label: 'Check-out Date', defaultVisible: true },
    { field: 'boarding', label: 'Boarding', defaultVisible: true },
    { field: 'boardingSurcharge', label: 'Boarding Surcharge', defaultVisible: true },
    { field: 'training', label: 'Training', defaultVisible: true },
    { field: 'hasBlanket', label: 'Blanket', defaultVisible: true },
    { field: 'isBillable', label: 'Billable', defaultVisible: true },
    { field: 'location', label: 'Location', defaultVisible: true },
    { field: 'actions', label: 'Actions', defaultVisible: true },
  ];

  const { sortField, sortDirection, handleSort, getSortedData } = useTableSort<BSHorse>('name');
  const { toggleColumn, isColumnVisible } = useColumnVisibility<BSHorse>(columns);
  const { searchQuery, setSearchQuery, getFilteredData } = useTableSearch<BSHorse>();

  useEffect(() => {
    const fetchData = () => {
      const q = query(collection(db, collectionName));
      onSnapshot(q, (querySnapshot) => {
        const objectsArray: BSHorse[] = [];
        querySnapshot.forEach((doc) => {
          const horseData = doc.data() as BSHorse;
          const checkInDate = horseData.checkInDate instanceof Timestamp
            ? horseData.checkInDate.toDate() 
            : horseData.checkInDate;

          const checkOutDate = horseData.checkOutDate instanceof Timestamp 
            ? horseData.checkOutDate.toDate() 
            : horseData.checkOutDate;

          objectsArray.push({ ...horseData, id: doc.id, checkInDate, checkOutDate });
        });
        setObjects(objectsArray);
      });
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = () => {
      const q = query(collection(db, "customers"), orderBy("name"))
        onSnapshot(q, (querySnapshot) => {
          const customersArray: BSCustomer[] = [];
          querySnapshot.forEach((doc) => {
            const customerData = doc.data() as BSCustomer;
            customersArray.push({ ...customerData, id: doc.id });
          });
          setCustomers(customersArray);
        }
      );
    }

    fetchData();
  }, []);

  const saveObject = async () => {
    if (newObject.id === "") {
      const newDocRef = doc(collection(db, collectionName));
      newObject.id = newDocRef.id;
      await setDoc(newDocRef, newObject);
    } else {
      await updateDoc(doc(db, collectionName, newObject.id), newObject);
    }

    setIsDialogOpen(false);
    resetForm();
  };

  const editObject = (object: BSHorse) => {
    setNewObject(object);
    setIsDialogOpen(true);
  };

  const deleteObject = async (object: BSHorse) => {
    await deleteDoc(doc(db, collectionName, object.id));
  };

  const resetForm = () => {
    setNewObject(emptyObject);
  };

  const getFilteredHorses = () => {
    let filtered = objects;
    
    // Filter by in-property status
    if (inProperty) {
      const currentDate = new Date();
      filtered = filtered.filter(horse => {
        const isAfterCheckIn = horse.checkInDate <= currentDate;
        const isBeforeCheckOut = !horse.checkOutDate || horse.checkOutDate >= currentDate;
        return isAfterCheckIn && isBeforeCheckOut;
      });
    }

    // Apply search filter
    filtered = getFilteredData(filtered, (horse) => [
      horse.name,
      customers.find(c => c.id === horse.customerId)?.name || '',
      horse.location,
      horse.checkInDate.toLocaleDateString(),
      horse.checkOutDate?.toLocaleDateString() || '',
      formatCurrency(horse.boarding)
    ]);

    // Apply sorting
    return getSortedData(filtered, sortField === 'customerId' ? (a, b) => {
      const aName = customers.find(c => c.id === a.customerId)?.name || '';
      const bName = customers.find(c => c.id === b.customerId)?.name || '';
      return sortDirection === 'asc' 
        ? aName.localeCompare(bName)
        : bName.localeCompare(aName);
    } : undefined);
  };

  const SortableHeader = ({ field, children }: { field: SortField, children: React.ReactNode }) => {
    const isCurrent = sortField === field;
    return (
      <TableHeader 
        className="cursor-pointer select-none group hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <span className="inline-flex">
            {isCurrent ? (
              sortDirection === 'asc' ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )
            ) : (
              <ChevronUpDownIcon className="w-4 h-4 opacity-0 group-hover:opacity-50" />
            )}
          </span>
        </div>
      </TableHeader>
    );
  };

  const billHorses = async (horses: BSHorse[]) => {
    setIsBilling(true);
    const batch = writeBatch(db);

    try {
      for (const horse of horses) {
        // Create bill items
        const billItems: Omit<BSBillItem, 'id'>[] = [
          // Boarding
          {
            customerId: horse.customerId,
            horseId: horse.id,
            serviceId: 'EHerXJf9v0Y9JnnFSRta',
            date: new Date(),
            quantity: 1,
            value: horse.boarding,
            location: horse.location,
            exported: false
          },
          // Boarding Surcharge
          {
            customerId: horse.customerId,
            horseId: horse.id,
            serviceId: 'm1V8EWKGyhfz2azMklFK',
            date: new Date(),
            quantity: 1,
            value: horse.boardingSurcharge,
            location: horse.location,
            exported: false
          }
        ];

        // Add tax if in Long Island
        if (horse.location === 'long-island') {
          billItems.push({
            customerId: horse.customerId,
            horseId: horse.id,
            serviceId: 'URVTLYhRyZJpgmbrmxxj',
            date: new Date(),
            quantity: 1,
            value: horse.boarding * 0.0863,
            location: horse.location,
            exported: false
          });
        }

        // Add blanket if applicable
        if (horse.hasBlanket) {
          billItems.push({
            customerId: horse.customerId,
            horseId: horse.id,
            serviceId: 'XhYTtwEAfXDpLWkhVjBC',
            date: new Date(),
            quantity: 1,
            value: 100,
            location: horse.location,
            exported: false
          });
        }

        // Add training if applicable
        if (horse.training > 0) {
          billItems.push({
            customerId: horse.customerId,
            horseId: horse.id,
            serviceId: 'zkyoLrILKFNtuhud7l2G',
            date: new Date(),
            quantity: 1,
            value: horse.training,
            location: horse.location,
            exported: false
          });
        }

        // Add bill items to batch
        for (const item of billItems) {
          const newBillRef = doc(collection(db, 'billItems'));
          batch.set(newBillRef, { ...item, id: newBillRef.id });
        }
      }

      await batch.commit();
      setIsBillDialogOpen(false);
    } catch (error) {
      console.error('Error billing horses:', error);
      // TODO: Add error handling
    } finally {
      setIsBilling(false);
    }
  };

  // Add toggle billable function
  const toggleBillable = async (horse: BSHorse) => {
    try {
      await updateDoc(doc(db, 'horses', horse.id), {
        isBillable: !horse.isBillable
      });
    } catch (error) {
      console.error('Error toggling billable status:', error);
      // TODO: Add error handling
    }
  };

  const updateCheckInDate = async (horse: BSHorse, date: Date | null) => {
    try {
      await updateDoc(doc(db, 'horses', horse.id), {
        checkInDate: date
      });
    } catch (error) {
      console.error('Error updating check-in date:', error);
    }
  };

  const updateCheckOutDate = async (horse: BSHorse, date: Date | null) => {
    try {
      await updateDoc(doc(db, 'horses', horse.id), {
        checkOutDate: date
      });
    } catch (error) {
      console.error('Error updating check-out date:', error);
    }
  };

  return (
    <div>
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Horses</Heading>
        <div className="flex items-center gap-4">
          <SwitchField>
            <Label>Showing {inProperty ? 'horses in property' : 'all horses'}</Label>
            <Switch color="green" checked={inProperty} onChange={setInProperty} />
          </SwitchField>
          <div className="relative">
            <Input
              type="search"
              placeholder="Search horses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-[250px]"
            />
          </div>
          <Dropdown>
            <DropdownButton>
              <EyeIcon className="w-5 h-5 mr-2" />
              Columns
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              {columns.map(({ field, label }) => (
                <DropdownItem
                  key={field}
                  onClick={() => toggleColumn(field)}
                >
                  <div className="flex items-center gap-2">
                    {isColumnVisible(field) ? (
                      <EyeIcon className="w-4 h-4" />
                    ) : (
                      <EyeSlashIcon className="w-4 h-4" />
                    )}
                    {label}
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
          >
            Add Horse
          </Button>
        </div>
      </div>
      {getFilteredHorses().length > 0 ? (
        <>
          <Table
            dense
            striped
            className="w-full [--gutter:theme(spacing.6)] sm:[--gutter:theme(spacing.8)]"
          >
            <TableHead>
              <TableRow>
                {columns.map(({ field, label }) => 
                  isColumnVisible(field) && (
                    field === 'actions' ? (
                      <TableHeader key={field}>Actions</TableHeader>
                    ) : (
                      <SortableHeader key={field} field={field as SortField}>
                        {label}
                      </SortableHeader>
                    )
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredHorses().map((object) => (
                <TableRow key={object.id}>
                  {isColumnVisible('name') && (
                    <TableCell className="font-medium">{object.name}</TableCell>
                  )}
                  {isColumnVisible('customerId') && (
                    <TableCell>
                      {customers.find(customer => customer.id === object.customerId)?.name || 'Unknown'}
                    </TableCell>
                  )}
                  {isColumnVisible('checkInDate') && (
                    <TableCell>
                      <InlineDatePicker
                        value={object.checkInDate}
                        onChange={(date) => updateCheckInDate(object, date)}
                      />
                    </TableCell>
                  )}
                  {isColumnVisible('checkOutDate') && (
                    <TableCell>
                      <InlineDatePicker
                        value={object.checkOutDate}
                        onChange={(date) => updateCheckOutDate(object, date)}
                      />
                    </TableCell>
                  )}
                  {isColumnVisible('boarding') && (
                    <TableCell>{formatCurrency(object.boarding)}</TableCell>
                  )}
                  {isColumnVisible('boardingSurcharge') && (
                    <TableCell>{formatCurrency(object.boardingSurcharge)}</TableCell>
                  )}
                  {isColumnVisible('training') && (
                    <TableCell>{formatCurrency(object.training)}</TableCell>
                  )}
                  {isColumnVisible('hasBlanket') && (
                    <TableCell>{object.hasBlanket ? "$100.00" : "$0.00"}</TableCell>
                  )}
                  {isColumnVisible('isBillable') && (
                    <TableCell>
                      <Checkbox
                        checked={object.isBillable}
                        onChange={() => toggleBillable(object)}
                      />
                    </TableCell>
                  )}
                  {isColumnVisible('location') && (
                    <TableCell>{object.location === 'florida' ? 'Florida' : 'Long Island'}</TableCell>
                  )}
                  {isColumnVisible('actions') && (
                    <TableCell>
                      <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                        <Dropdown>
                          <DropdownButton plain aria-label="More options">
                            <EllipsisHorizontalIcon />
                          </DropdownButton>
                          <DropdownMenu anchor="bottom end">
                            <DropdownItem onClick={() => editObject(object)}>
                              Edit
                            </DropdownItem>
                            <DropdownDivider />
                            <DropdownItem onClick={() => deleteObject(object)}>
                              Delete
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex w-full flex-wrap items-center justify-between gap-4 border-t border-zinc-950/10 pt-6 dark:border-white/10">
            <div>
              <Button 
                onClick={() => {
                  const billableHorses = getFilteredHorses().filter(horse => horse.isBillable);
                  if (billableHorses.length > 0) {
                    setIsBillDialogOpen(true);
                  }
                }}
                disabled={isBilling || getFilteredHorses().filter(horse => horse.isBillable).length === 0}
              >
                {isBilling ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    Billing...
                  </>
                ) : (
                  `Bill ${getFilteredHorses().filter(horse => horse.isBillable).length} Horses`
                )}
              </Button>
            </div>
            <div className="flex items-center gap-4">
              {/* Add any additional footer actions here */}
            </div>
          </div>
        </>
      ) : (
        <EmptyState
          title={"No Horses"}
          description={"Add new horses to manage them here"}
        >
          <PlusIcon className="w-10 h-10" />
        </EmptyState>
      )}

      <Dialog open={isDialogOpen} onClose={setIsDialogOpen}>
        <DialogTitle>New Horse</DialogTitle>
        <DialogBody>
          <Fieldset>
            <div
              data-slot="control"
              className="grid grid-cols-1 items-center gap-x-4 gap-y-6 sm:grid-cols-3"
            >
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Name</Label>
                <Input
                  type="text"
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      name: e.target.value,
                    })
                  }
                  value={newObject.name}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="name"
                />
              </Headless.Field>

              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Responsible Party</Label>
                <Select
                  value={newObject.customerId}
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      customerId: e.target.value,
                    })
                  }
                  className="mt-3 sm:col-span-2 sm:mt-0"
                >
                  <option value="">Select a responsible party</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </Select>
              </Headless.Field>

              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Check-in Date</Label>
                <input
                  type="date"
                  id="check-in"
                  name="check-in date"
                  value={formatDateToInputString(newObject.checkInDate)}
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      checkInDate: new Date(`${e.target.value}T00:00:00`),
                    })
                  }
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Check-out Date</Label>
                <input
                  type="date"
                  id="check-out"
                  name="check-out date"
                  value={formatDateToInputString(newObject.checkOutDate)}
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      checkOutDate: new Date(`${e.target.value}T00:00:00`),
                    })
                  }
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Boarding</Label>
                <Input
                  type="number"
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      boarding: parseInt(e.target.value),
                    })
                  }
                  value={newObject.boarding}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="boarding"
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Boarding Surcharge</Label>
                <Input
                  type="number"
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      boardingSurcharge: parseInt(e.target.value),
                    })
                  }
                  value={newObject.boardingSurcharge}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="boarding-surcharge"
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Training</Label>
                <Input
                  type="number"
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      training: parseInt(e.target.value),
                    })
                  }
                  value={newObject.training}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="training"
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Blanket</Label>
                <Checkbox
                  checked={newObject.hasBlanket}
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      hasBlanket: e,
                    })
                  }
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Billable</Label>
                <Checkbox
                  checked={newObject.isBillable}
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      isBillable: e,
                    })
                  }
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Location</Label>
                <Select
                  value={newObject.location}
                  onChange={(e) => setNewObject({ ...newObject, location: e.target.value as HorseLocation })}
                >
                  <option value="florida">Florida</option>
                  <option value="long-island">Long Island</option>
                </Select>
              </Headless.Field>
            </div>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => saveObject()}>
            {newObject.id === "" ? "Add" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Billing Confirmation Dialog */}
      <Alert 
        open={isBillDialogOpen} 
        onClose={() => !isBilling && setIsBillDialogOpen(false)}
      >
        <AlertTitle>Confirm Billing</AlertTitle>
        <AlertDescription>
          Are you sure you want to bill {getFilteredHorses().filter(horse => horse.isBillable).length} horses? 
          This action cannot be undone.
        </AlertDescription>
        <AlertActions>
          <Button 
            plain 
            onClick={() => setIsBillDialogOpen(false)}
            disabled={isBilling}
          >
            Cancel
          </Button>
          <Button
            onClick={() => billHorses(getFilteredHorses().filter(horse => horse.isBillable))}
            disabled={isBilling}
          >
            {isBilling ? 'Billing...' : 'Confirm'}
          </Button>
        </AlertActions>
      </Alert>
    </div>
  );
}
